import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationProduct } from '../../models/notifications/notification-catalog-entry.model';
import { NotificationDelivery } from '../../models/notifications/notification-delivery.model';
import { QueryOptions } from '../../models/query-options/query-options.class';
import { APIService } from '../../services/api/api.service';
import { PagedFeed } from '../../services/api/notifications/notifications.service';
import { NotificationBellService } from '../../services/notification-bell.service';
import { NotificationService } from '../../services/notification.service';
import { relativeTime, resolveTimeLabel, severityIcon } from './notification-time.util';

/**
 * Header bell (spec §11.2). Hidden while the Notification Center is off for this API
 * (`bell.enabled$`), badge and panel driven entirely by `NotificationBellService`.
 *
 * `notification`, like `cdr`, is a REQUIRED constructor parameter — the same ruling from this
 * plan's adversarial review that made `cdr` required applies here: an optional DI dependency is
 * an untested shortcut, because a spec that omits it never proves the code path that uses it.
 * The component spec passes a `{ error: jest.fn(), success: jest.fn() }` stub, the same way it
 * stubs `cdr`.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatMenuModule, MatTooltipModule, TranslateModule],
  selector: 'app-notification-bell',
  standalone: true,
  styleUrls: ['./notification-bell.component.scss'],
  templateUrl: './notification-bell.component.html'
})
export class NotificationBellComponent implements OnDestroy {
  public items: NotificationDelivery[] = [];
  public loading: boolean = false;

  private readonly destroy$: Subject<void> = new Subject<void>();

  public constructor(
    private readonly api: APIService,
    public readonly bell: NotificationBellService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly notification: NotificationService,
    // Required, like cdr and notification: the relative time on every row is a translated
    // sentence, and an optional TranslateService would let that quietly fall back to keys.
    private readonly translate: TranslateService
  ) {}

  /**
   * Bound for the template so `{{ relativeTime(item.createdAt) }}` reads naturally. The bucket is
   * computed client-side (spec §12) and the WORDING comes from the translation file — this
   * dashboard ships 14 languages, and this string used to be hardcoded English.
   */
  public relativeTime(date: Date): string {
    return resolveTimeLabel(relativeTime(date), this.translate);
  }

  /** Bound for the template so `[ngClass]="severityIcon(item.severity)"` reads naturally. */
  public severityIcon(severity: string): string {
    return severityIcon(severity);
  }

  /**
   * The design's one-line source: "4Comply - Bulk upload completed". The product half falls away
   * rather than rendering a placeholder when the catalog has not arrived or does not carry the
   * event, which is also why `labelFor` degrades to the raw event type instead of an empty line.
   */
  public sourceLine(item: NotificationDelivery): string {
    const label: string = this.bell.labelFor(item.eventType);
    const product: NotificationProduct | undefined = this.bell.productOf(item.eventType);
    return product ? `${product.label} · ${label}` : label;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Loads the last 10 deliveries every time the panel opens, newest first. */
  public onOpened(): void {
    const qo: QueryOptions = new QueryOptions();
    qo.page = 1;
    qo.pageSize = 10;
    qo.sorts = '-created_at';
    this.loading = true;
    this.api.notifications.getFeed(qo, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => {
          this.loading = false;
          this.cdr.markForCheck();
        },
        next: (page: PagedFeed): void => {
          this.items = page.list;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Optimistic mark-read with rollback: the row only stays "read" if the API agrees. On failure
   * it reverts to "sent" and shows a translated error — nothing stays "done" on screen that the
   * server refused.
   */
  public open(item: NotificationDelivery): void {
    if (item.status === 'sent') {
      item.status = 'read';
      this.api.notifications.markRead(item.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (): void => {
            item.status = 'sent';
            this.notification.error('NOTIFICATIONS.MARK_READ_FAILED');
            this.cdr.markForCheck();
          },
          next: (): void => this.bell.refresh()
        });
    }
    this.router.navigate(['/notifications'], { queryParams: { open: item.id } });
  }

  public markAll(): void {
    this.api.notifications.markAllRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => this.notification.error('NOTIFICATIONS.MARK_ALL_FAILED'),
        next: (): void => {
          this.items = this.items.map(
            (item: NotificationDelivery): NotificationDelivery => ({ ...item, status: 'read' } as NotificationDelivery)
          );
          this.bell.refresh();
          this.cdr.markForCheck();
        }
      });
  }

  public goToAll(): void {
    this.router.navigate(['/notifications']);
  }

  public goToSettings(): void {
    this.router.navigate(['/settings'], { queryParams: { tab: 'notifications' } });
  }

  public trackById(_index: number, item: NotificationDelivery): string {
    return item.id;
  }
}
